import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserPlus, User, Mail, Lock, FileText, ArrowRight } from 'lucide-react';

const RegisterForm = ({ onClose, switchToLogin }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    ageConfirmed: false,
    termsAccepted: false
  });
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      return;
    }
    if (!formData.ageConfirmed) {
      alert('You must confirm you are 18 years or older to register.');
      return;
    }
    if (!formData.termsAccepted) {
      alert('You must accept the Terms & Conditions to register.');
      return;
    }

    setLoading(true);
    const result = await register(
      formData.username,
      formData.email,
      formData.password,
      formData.fullName
    );
    setLoading(false);
    if (result.success) {
      onClose();
      if (result.corporate) navigate('/my-aon');
    }
  };

  const inputClass = "w-full h-11 pl-10 pr-3 bg-[#1a1a1a] border border-[#333] rounded-lg text-[#F5F5DC] text-base placeholder:text-gray-500 focus:outline-none focus:border-[#F5F5DC] transition-colors";
  const labelClass = "flex items-center gap-2 text-sm text-gray-300 mb-1.5";

  return (
    <div className="w-full text-[#F5F5DC]" data-testid="register-form">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#1a1a1a] border border-[#333] mb-4">
          <UserPlus className="h-6 w-6 text-[#F5F5DC]" />
        </div>
        <h2 className="text-2xl md:text-3xl text-[#F5F5DC] mb-2">Create Your Free Account</h2>
        <p className="text-sm text-gray-400">Access all features. No payment required.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="fullName" className={labelClass}><FileText className="h-4 w-4" /> Full Name <span className="text-gray-500">(optional)</span></label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input id="fullName" name="fullName" type="text" placeholder="Enter your name"
              value={formData.fullName} onChange={handleChange} className={inputClass} data-testid="register-fullname" />
          </div>
        </div>

        <div>
          <label htmlFor="username" className={labelClass}><User className="h-4 w-4" /> Username</label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input id="username" name="username" type="text" placeholder="Choose a username"
              value={formData.username} onChange={handleChange} className={inputClass} required data-testid="register-username" />
          </div>
        </div>

        <div>
          <label htmlFor="email" className={labelClass}><Mail className="h-4 w-4" /> Email Address</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input id="email" name="email" type="email" placeholder="you@example.com"
              value={formData.email} onChange={handleChange} className={inputClass} required data-testid="register-email" />
          </div>
        </div>

        <div>
          <label htmlFor="password" className={labelClass}><Lock className="h-4 w-4" /> Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input id="password" name="password" type="password" placeholder="Create a password"
              value={formData.password} onChange={handleChange} className={inputClass} required data-testid="register-password" />
          </div>
          <p className="text-xs text-gray-500 mt-1.5">Minimum 8 characters</p>
        </div>

        <div>
          <label htmlFor="confirmPassword" className={labelClass}><Lock className="h-4 w-4" /> Confirm Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input id="confirmPassword" name="confirmPassword" type="password" placeholder="Confirm your password"
              value={formData.confirmPassword} onChange={handleChange} className={inputClass} required data-testid="register-confirm-password" />
          </div>
          {formData.password !== formData.confirmPassword && formData.confirmPassword && (
            <p className="text-xs text-red-400 mt-1.5">Passwords do not match</p>
          )}
        </div>

        <div className="flex items-center gap-6 pt-1">
          <label htmlFor="ageConfirmed" className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input type="checkbox" id="ageConfirmed" name="ageConfirmed" checked={formData.ageConfirmed} onChange={handleChange}
              className="h-4 w-4 rounded border-[#444] bg-[#1a1a1a] accent-[#F5F5DC]" required data-testid="register-age-checkbox" />
            I am <strong className="text-[#F5F5DC]">18 or older</strong>
          </label>
          <label htmlFor="termsAccepted" className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input type="checkbox" id="termsAccepted" name="termsAccepted" checked={formData.termsAccepted} onChange={handleChange}
              className="h-4 w-4 rounded border-[#444] bg-[#1a1a1a] accent-[#F5F5DC]" required data-testid="register-terms-checkbox" />
            I accept the <Link to="/terms" target="_blank" onClick={(e) => e.stopPropagation()} className="text-[#F5F5DC] underline underline-offset-2 hover:text-white">Terms</Link>
          </label>
        </div>

        <button type="submit" disabled={loading || formData.password !== formData.confirmPassword || !formData.ageConfirmed || !formData.termsAccepted}
          data-testid="register-submit"
          className="w-full h-12 flex items-center justify-center gap-2 bg-[#F5F5DC] text-[#0a0a0a] rounded-lg text-base font-medium hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          {loading ? 'Creating Account...' : <>Create Free Account <ArrowRight className="h-4 w-4" /></>}
        </button>

        <div className="relative my-1">
          <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-[#333]" /></div>
          <div className="relative flex justify-center"><span className="bg-[#0f0f0f] px-3 text-xs uppercase tracking-wide text-gray-500">Or</span></div>
        </div>

        <button type="button" onClick={loginWithGoogle} data-testid="register-google"
          className="w-full h-12 flex items-center justify-center gap-2 bg-transparent border border-[#333] rounded-lg text-base text-[#F5F5DC] hover:bg-[#1a1a1a] transition-colors">
          <svg className="h-4 w-4" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <p className="text-center text-sm text-gray-400 pt-1">
          Already have an account?{' '}
          <button type="button" onClick={switchToLogin} className="text-[#F5F5DC] underline underline-offset-2 hover:text-white" data-testid="register-switch-login">Login here</button>
        </p>
      </form>
    </div>
  );
};

export default RegisterForm;
